import React, { useState, useCallback } from 'react'
import { Stack, Grid } from '@mui/material'
import {
  DateTimeInput,
  required,
  maxLength,
  TextInput,
  FormTab,
  BooleanInput,
  TransformData,
} from 'react-admin'
import dayjs from 'dayjs'
import { MyRichTextInput } from '../../components/text-editor'
import { isAfterDate, isBeforeDate, isSameDate, isYoutubeLink } from '../../utils/validators'
import { TabbedMultiCardForm } from '../../components/tab'
import { ApproveAndPublishToolBar } from '../../components/form'
import { CreateBase } from '../../components/base'
import { COMMON_FIELDS, PROMOTIONS_FIELDS } from '../../providers/localization/constant'
import { ImageInput } from '../../components/inputs'
import { ImageField } from '../../components/fields'
import ApplyToEnglishButton from '../../components/buttons/ApplyToEnglishButton'
import { ThumbnailInput } from '../../components/inputs/ThumbnailInput'
import sanitizeHtml from 'sanitize-html'
import trim from 'lodash/trim'
import unescape from 'lodash/unescape'

const RESOURCE_PREFIX = 'resources.promotions.create'

const PublishPeriodInputs: React.FC = () => {
  const defaultPublishDate = dayjs()
  const defaultExpiredDate = defaultPublishDate.add(2, 'day')

  return (
    <>
      <DateTimeInput
        source='start_date'
        label={`${COMMON_FIELDS}.date.published_on`}
        fullWidth
        defaultValue={defaultPublishDate}
        validate={[
          required(),
          isBeforeDate('end_date', 'The publish date must be before the expired date.'),
        ]}
      />
      <DateTimeInput
        source='end_date'
        label={`${COMMON_FIELDS}.date.expired_on`}
        fullWidth
        defaultValue={defaultExpiredDate}
        validate={[
          required(),
          isAfterDate('start_date', 'The expired date must be after the publish date.'),
          isSameDate(),
        ]}
      />
    </>
  )
}

const PersistForm: React.FC = () => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} order={{ xs: -1, sm: 0 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInput
              sx={{ display: 'none' }}
              source='order'
              label={`${COMMON_FIELDS}.order`}
              fullWidth
              defaultValue={0}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
              <PublishPeriodInputs />
            </Stack>
          </Grid>
          <Grid item sm={6}>
            <BooleanInput
              source='is_max_card'
              label={`${PROMOTIONS_FIELDS}.max_card.label`}
              sx={{
                '& .MuiFormControlLabel-root': { marginRight: 'auto' },
              }}
            />
          </Grid>
          <Grid item sm={6}>
            <BooleanInput
              source='is_credit_card'
              label={`${PROMOTIONS_FIELDS}.credit_card.label`}
              sx={{
                '& .MuiFormControlLabel-root': { marginRight: 'auto' },
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInput
              source='youtube_link'
              label='Youtube Link'
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              validate={[isYoutubeLink()]}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <TextInput
          source='slug_th'
          label={`${COMMON_FIELDS}.slug_th.label`}
          InputProps={{
            fullWidth: true,
          }}
          fullWidth
          validate={[required()]}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          source='slug_en'
          label={`${COMMON_FIELDS}.slug_en.label`}
          InputProps={{
            fullWidth: true,
          }}
          fullWidth
          validate={[required()]}
        />
      </Grid>
      <Grid item xs={12}>
        <ThumbnailInput
          source='thumbnail'
          label={`${PROMOTIONS_FIELDS}.thumbnail_path_helper`}
          accept='image/*'
        >
          <ImageField source='src' title='title' />
        </ThumbnailInput>
      </Grid>
      <Grid item xs={12}>
        <ImageInput
          source='images'
          label={`${PROMOTIONS_FIELDS}.images.label`}
          helperText={`${PROMOTIONS_FIELDS}.images_helper`}
          accept='image/*'
          multiple
          initCrop={false}
        ></ImageInput>
      </Grid>
    </Grid>
  )
}

const PromotionCreate: React.FC = () => {
  const [editorDescriptionEnKey, setEditorDescriptionEnKey] = useState(0)
  const reloadTextEditor = (): void => {
    setEditorDescriptionEnKey(editorDescriptionEnKey + 1)
  }
  const transform: TransformData = useCallback((data) => {
    return {
      ...data,
      title_th: sanitizeHtml(unescape(data.title_th)),
      title_en: sanitizeHtml(unescape(data.title_en)),
      short_description_th: sanitizeHtml(unescape(data.short_description_th)),
      short_description_en: sanitizeHtml(unescape(data.short_description_en)),
      description_th: sanitizeHtml(unescape(data.description_th)),
      description_en: sanitizeHtml(unescape(data.description_en)),
      slug_th: trim(data.slug_th),
      slug_en: trim(data.slug_en),
    }
  }, [])
  return (
    <CreateBase
      title={`${RESOURCE_PREFIX}.title`}
      listAction={{
        hasBackButton: true,
      }}
      noCard
      encType='multipart/form-data'
      transform={transform}
    >
      <TabbedMultiCardForm
        childrenForms={[<PersistForm key='promotion-info-form' />]}
        formProps={{
          toolbar: null,
          toolbarBottom: <ApproveAndPublishToolBar encType='multipart/form-data' />,
        }}
      >
        <FormTab label='TH'>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextInput
                source='title_th'
                label={`${PROMOTIONS_FIELDS}.title_th`}
                InputProps={{
                  fullWidth: true,
                }}
                fullWidth
                validate={[required(), maxLength(255)]}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source='short_description_th'
                label={`${PROMOTIONS_FIELDS}.short_description_th`}
                InputProps={{
                  fullWidth: true,
                }}
                fullWidth
                multiline
                rows={3}
                validate={[required()]}
              />
            </Grid>
            <Grid item xs={12}>
              <MyRichTextInput
                fullWidth
                label={`${PROMOTIONS_FIELDS}.description_th`}
                source='description_th'
                validate={[required()]}
              />
            </Grid>
            <ApplyToEnglishButton updateKeyEditor={reloadTextEditor} />
          </Grid>
        </FormTab>
        <FormTab label='EN'>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextInput
                source='title_en'
                label={`${PROMOTIONS_FIELDS}.title_en`}
                InputProps={{
                  fullWidth: true,
                }}
                fullWidth
                validate={[required(), maxLength(255)]}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source='short_description_en'
                label={`${PROMOTIONS_FIELDS}.short_description_en`}
                InputProps={{
                  fullWidth: true,
                }}
                fullWidth
                multiline
                rows={3}
                validate={[required()]}
              />
            </Grid>
            <Grid item xs={12}>
              <MyRichTextInput
                key={editorDescriptionEnKey}
                label={`${PROMOTIONS_FIELDS}.description_en`}
                source='description_en'
                fullWidth
                validate={[required()]}
              />
            </Grid>
          </Grid>
        </FormTab>
      </TabbedMultiCardForm>
    </CreateBase>
  )
}

export default PromotionCreate
